import moment from 'moment'
import { format } from 'date-fns'

import { formatDateTimeInTimezone } from '@/utils/date'

import StatusPill from '../../StatusPill/StatusPill'
import Priority from '../../Priority/Priority'
import Icon from '../../Icons'

import AddressCell from './components/AddressCell'
import ResidentCell from './components/ResidentCell'
import AssigneeCell from './components/AssigneeCell'

export const getColumns = (t, isYardiProperty, timezone) => {
  const priorityColumn = isYardiProperty
    ? [
        {
          Header: t('maintenance:requests.columns.priority'),
          accessor: 'priority',
          id: 'priority',
          Cell: ({ value }) => (value && <Priority value={value} />) || '—',
        },
      ]
    : []

  return [
    {
      Header: t('maintenance:requests.columns.idNumber'),
      accessor: 'id',
      id: 'idNumber',
    },
    {
      accessor: 'updatedAt',
      id: 'Date',
      Header: () => {
        return (
          <div style={{ display: 'flex', flexDirection: 'row', cursor: 'pointer' }}>
            {t('maintenance:requests.columns.updatedOn')}
            <span style={{ width: 20, height: 20, position: 'relative', top: 5, left: 5 }}>
              <Icon icon="upDownArrows" />
            </span>
          </div>
        )
      },
      Cell: ({ value }) => moment(Number(value)).format('MM/DD/YY'),
    },
    {
      Header: t('maintenance:requests.columns.requestObject'),
      id: 'requestObject',

      Cell: ({ row }) => <ResidentCell row={row} />,
    },
    {
      Header: t('maintenance:requests.columns.address'),
      accessor: 'id',
      id: 'address',

      Cell: ({ row }) => <AddressCell row={row} />,
    },
    {
      Header: t('maintenance:requests.columns.assignee'),
      accessor: 'assignee',
      id: 'assignee',

      Cell: ({ value }) => <AssigneeCell assignee={value} />,
    },
    {
      Header: t('maintenance:requests.columns.scheduledDate'),
      accessor: 'scheduledDate',
      id: 'scheduledDate',
      Cell: ({ value }) => (value ? formatDateTimeInTimezone(value, timezone) : '—'),
    },
    ...priorityColumn,
    {
      Header: t('maintenance:requests.columns.status'),
      accessor: 'status',
      id: 'status',
      Cell: ({ value }) => (value && <StatusPill title={value} />) || '—',
    },
  ]
}

export const getMaintenanceRequestTabs = (t) => [
  {
    id: 0,
    text: t('maintenance:requests.tabs.all'),
    value: '',
  },
  {
    id: 1,
    text: t('maintenance:requests.tabs.pending'),
    value: 'pending',
  },
  {
    id: 2,
    text: t('maintenance:requests.tabs.scheduled'),
    value: 'scheduled',
  },
  {
    id: 3,
    text: t('maintenance:requests.tabs.completed'),
    value: 'completed',
  },
]

export const getMaintenanceRequestStatuses = (t) => [
  {
    id: 0,
    text: t('maintenance:requests.filters.statusOptions.inProgress'),
    value: 'In Progress',
  },
  {
    id: 1,
    text: t('maintenance:requests.filters.statusOptions.onHold'),
    value: 'On Hold',
  },
  {
    id: 2,
    text: t('maintenance:requests.filters.statusOptions.canceled'),
    value: 'Canceled',
  },
  {
    id: 3,
    text: t('maintenance:requests.filters.statusOptions.finished'),
    value: 'Finished',
  },
  {
    id: 4,
    text: t('maintenance:requests.filters.statusOptions.pending'),
    value: 'Pending',
  },
]

export const getMaintenanceRequestPriorities = (t) => [
  {
    id: 0,
    text: t('maintenance:requests.filters.priorityOptions.emergency'),
    value: 'Emergency',
  },
  {
    id: 1,
    text: t('maintenance:requests.filters.priorityOptions.critical'),
    value: 'Critical',
  },
  {
    id: 2,
    text: t('maintenance:requests.filters.priorityOptions.high'),
    value: 'High',
  },
  {
    id: 3,
    text: t('maintenance:requests.filters.priorityOptions.medium'),
    value: 'Med',
  },
  {
    id: 4,
    text: t('maintenance:requests.filters.priorityOptions.low'),
    value: 'Low',
  },
  {
    id: 5,
    text: t('maintenance:requests.filters.priorityOptions.routine'),
    value: 'Routine',
  },
]

export const maintenanceRequestTypes = [
  'Plumbing',
  'Lighting',
  'Windows',
  'HVAC',
  'Doors',
  'Floors',
  'Appliances',
  'Other',
]

export const maintenanceRealPageLocationTypes = [
  { id: 1, text: 'Unit', value: 'unit' },
  { id: 4, text: 'Common Area', value: 'common_area' },
]

export const maintenanceRequestStatusTypes = [
  'In Progress',
  'On Hold',
  'Canceled',
  'Finished',
  'Pending',
]

export const booleanList = ['False', 'True']

// Note: Yardi's XML → JSON conversion turns numeric-looking names (e.g. a subcategory "1") into
// numbers, so coerce to string instead of assuming one (EL-15919)
export const parseToSelect = (input) => {
  const array = Array.isArray(input) ? input : [input]
  return array
    .filter((value) => value !== null && value !== undefined)
    .map((value, id) => {
      const text = String(value)
      return {
        text,
        id,
        value: text.toLowerCase().split(' ').join('_'),
      }
    })
}

export const parsePriorityToSelect = (priorities) => {
  if (!priorities) return []

  // Note: Handling for Yardi, which sometimes returns string if there is single priority setup
  if (typeof priorities === 'string') {
    return [{ text: priorities, id: 0, value: '0' }]
  }

  return priorities.map((value, index) => ({
    text: value,
    id: index,
    value: index.toString(),
  }))
}

export const parseLocationToSelect = (array) =>
  array.map(({ codeName, codeDisplayName }) => ({
    text: codeDisplayName,
    id: codeName,
    value: codeName,
  }))

export const getValue = (startDate, endDate) => {
  const formattedEndDate = endDate ? ` - ${format(endDate, 'MM/dd/yyyy')}` : ''
  return startDate ? `${format(startDate, 'MM/dd/yyyy')} ${formattedEndDate}` : ''
}

export const getAnalyticsConfig = (t) => [
  {
    value: '',
    type: 'Assign',
    timeText: t('maintenance:requests.analytics.timeTo'),
    description: t('maintenance:requests.analytics.approximateTime'),
    unit: 'time',
  },
  {
    value: '',
    type: 'Schedule',
    timeText: t('maintenance:requests.analytics.timeTo'),
    description: t('maintenance:requests.analytics.approximateTime'),
    unit: 'time',
  },
  {
    value: '',
    type: 'Complete',
    timeText: t('maintenance:requests.analytics.timeTo'),
    description: t('maintenance:requests.analytics.approximateTime'),
    unit: 'time',
  },
  {
    value: '',
    type: 'Active',
    timeText: t('maintenance:requests.analytics.openRequests'),
    description: t('maintenance:requests.analytics.forSelectedBuilding'),
    unit: 'count',
  },
  {
    value: '',
    type: 'Pending',
    timeText: t('maintenance:requests.analytics.openRequests'),
    description: t('maintenance:requests.analytics.forSelectedBuilding'),
    unit: 'count',
  },
  {
    value: '',
    type: 'Scheduled',
    timeText: t('maintenance:requests.analytics.openRequests'),
    description: t('maintenance:requests.analytics.forSelectedBuilding'),
    unit: 'count',
  },
  {
    value: '',
    type: 'Inactive',
    timeText: t('maintenance:requests.analytics.closedRequests'),
    description: t('maintenance:requests.analytics.forSelectedBuilding'),
    unit: 'count',
  },
]
